import React, { useEffect, useMemo, useRef } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Icon, useFormControl, useMultiStyleConfig } from "@chakra-ui/react";
import { useSelect } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOption } from "./select-option";
import { SelectOptionAccessory } from "./select-option-accessory";
import { useSelectPopover, SelectPopover } from "./select-popover";
import ClearSelectButton from "./clear-select-button";
import { SelectOptions } from "./select-options";
import { SelectEmptyState } from "./select-empty-state";
import { SelectToggleButton } from "./select-toggle-button";
import { SelectValue } from "./select-value";
import { Spinner } from "./spinner";
import { useVirtualItems } from "./hooks/use-virtual-items";
import { SelectOptionLabel } from "./select-option-label";
import { defaultIsOptionDisabled, defaultOptionLabel, defaultOptionValue, } from "./lib/select-helpers";
export function Select({ isDisabled = false, isLoading = false, isInvalid = false, isClearable = false, isOptionDisabled = defaultIsOptionDisabled, emptyOptionsMessage = "No options", options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, ...props }) {
    const formControl = useFormControl({
        isDisabled,
        isInvalid,
    });
    const popover = useSelectPopover();
    const selectedOption = useMemo(() => {
        if (!value) {
            return;
        }
        return options.find((option) => {
            return isEqual(optionValue(option), value);
        });
    }, [options, value]);
    const select = useSelect({
        isOpen: popover.isOpen,
        labelId: `${formControl.id}-label`,
        toggleButtonId: formControl.id,
        items: options,
        itemToString(option) {
            return option ? optionLabel(option) : "";
        },
        selectedItem: selectedOption ?? null,
        onSelectedItemChange({ selectedItem }) {
            if (selectedItem) {
                onChange(optionValue(selectedItem));
                return;
            }
            if (isClearable) {
                onChange(undefined);
            }
        },
        onIsOpenChange({ isOpen }) {
            if (isOpen) {
                popover.open();
            }
            else {
                popover.close();
            }
        },
    });
    const showClearButton = isClearable && Boolean(selectedOption);
    const optionsContainer = useRef(null);
    const { virtualItems, getVirtualItemKey, getVirtualItemIndex } = useVirtualItems({
        options,
        optionLabel,
    });
    const virtualizer = useVirtualizer({
        count: virtualItems.length,
        getItemKey: getVirtualItemKey,
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 32;
        },
    });
    useEffect(() => {
        const virtualIndex = getVirtualItemIndex(select.highlightedIndex);
        if (virtualIndex >= 0) {
            virtualizer.scrollToIndex(virtualIndex);
        }
    }, [select.highlightedIndex]);
    useEffect(() => {
        if (!popover.isOpen && select.highlightedIndex === -1) {
            virtualizer.scrollToOffset(0);
        }
    }, [popover.isOpen, select.highlightedIndex]);
    const accessory = selectedOption && typeof optionAccessory === "function"
        ? optionAccessory(selectedOption)
        : undefined;
    const styles = useMultiStyleConfig("Select");
    return (React.createElement(RemoveScroll, { enabled: popover.isOpen },
        React.createElement(SelectToggleButton, { "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], "aria-busy": isLoading ? "true" : undefined, "data-selected": value ? "true" : undefined, disabled: formControl.disabled, width: width, "data-testid": "select", ...select.getToggleButtonProps({
                ...popover.getTargetProps({
                    onFocus: formControl.onFocus,
                    onBlur: formControl.onBlur,
                }),
            }), ...props },
            accessory && React.createElement(SelectOptionAccessory, { accessory: accessory }),
            React.createElement(SelectValue, null, selectedOption ? optionLabel(selectedOption) : placeholder),
            isLoading && React.createElement(Spinner, { size: "sm" }),
            !isLoading &&
                (showClearButton ? (React.createElement(ClearSelectButton, { isDisabled: formControl.disabled, onClick: select.reset })) : (React.createElement(Icon, { as: ChevronDownIcon, __css: styles["icon"] })))),
        React.createElement(SelectPopover, { isOpen: popover.isOpen, "data-testid": "select-popover", ...select.getMenuProps({
                ...popover.getPopoverProps(),
                onKeyDown(event) {
                    if (event.key === "Escape") {
                        event.stopPropagation();
                    }
                },
            }) },
            virtualItems.length === 0 && (React.createElement(SelectEmptyState, null, emptyOptionsMessage)),
            virtualItems.length > 0 && (React.createElement(SelectOptions, { ref: optionsContainer, virtualizer: virtualizer, virtualItems: virtualItems }, (virtualItem, index) => {
                if (virtualItem.type === "option") {
                    const accessory = optionAccessory?.(virtualItem.option);
                    return (React.createElement(SelectOption, { isSelected: virtualItem.option === selectedOption, "data-index": index, ...select.getItemProps({
                            ref: virtualizer.measureElement,
                            item: virtualItem.option,
                            index: virtualItem.index,
                            disabled: isOptionDisabled(virtualItem.option),
                        }) },
                        accessory && (React.createElement(SelectOptionAccessory, { accessory: accessory })),
                        React.createElement(SelectOptionLabel, null, optionLabel(virtualItem.option))));
                }
                return null;
            })))));
}
